import { useEffect } from "react";
import WichitaFallsHeroSection from "./components/HeroSection";
import WichitaFallsStickyNav from "./components/StickyNav";
import WichitaFallsProcessingTimeSection from "./components/ProcessingTimeSection";
import WichitaFallsServicesSection from "./components/ServicesSection";
import WichitaFallsLocalAreaSection from "./components/LocalAreaSection";
import WichitaFallsTravelTimingSection from "./components/TravelTimingSection";
import WichitaFallsDestinationsSection from "./components/DestinationsSection";
import WichitaFallsTestimonialsSection from "./components/TestimonialsSection";
import WichitaFallsAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import WichitaFallsComparisonSection from "./components/ComparisonSection";
import WichitaFallsMistakesSection from "./components/MistakesSection";
import WichitaFallsHowItWorksSection from "./components/HowItWorksSection";
import WichitaFallsFAQSection, { faqs } from "./components/FAQSection";
import WichitaFallsHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import WichitaFallsFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function WichitaFallsPage() {
  useEffect(() => {
    document.title = "Wichita Falls TX Passport Services — Emergency & Expedited | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/wichita-falls/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Wichita Falls, TX — as fast as 24 hours. Sheppard AFB military families, MSU students, and North Texas professionals. A+ BBB rated registered State Dept. courier. No office visit required.");
    setMeta("geo.placename", "Wichita Falls, Texas");
    setMeta("geo.region", "US-TX");
    setMeta("geo.position", "33.9137;-98.4934");
    setMeta("ICBM", "33.9137, -98.4934");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Texas", item: "https://fastpassportcenter.com/texas/" },
          { "@type": "ListItem", position: 3, name: "Wichita Falls Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center serves Wichita Falls travelers remotely with emergency and expedited passport processing. Specializing in Sheppard AFB military families, MSU students, and North Texas professionals.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: { "@type": "City", name: "Wichita Falls", containedInPlace: { "@type": "State", name: "Texas" } },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: { "@type": "LocalBusiness", name: "Fast Passport Center" },
        areaServed: { "@type": "City", name: "Wichita Falls" },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: { "@type": "Answer", text: faq.answer },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      document.getElementById("structured-data")?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <WichitaFallsHeroSection />
      <WichitaFallsStickyNav />
      <WichitaFallsProcessingTimeSection />
      <WichitaFallsServicesSection />
      <WichitaFallsLocalAreaSection />
      <WichitaFallsTravelTimingSection />
      <WichitaFallsDestinationsSection />
      <WichitaFallsTestimonialsSection />
      <WichitaFallsAcceptanceFacilitiesSection />
      <WichitaFallsComparisonSection />
      <WichitaFallsMistakesSection />
      <WichitaFallsHowItWorksSection />
      <WichitaFallsFAQSection />
      <WichitaFallsHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/wichita-falls" state="TX" />
      <WichitaFallsFinalCTA />
    </div>
  );
}